(function () {
  "use strict";

  const ready = (fn) => {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  };

  ready(() => {
    document.querySelectorAll(".sp-doc-lib").forEach((block) => {
      const url         = block.dataset.url || "";
      const title       = block.dataset.title || "SharePoint Document Library";
      const libraryName = block.dataset.libraryname || "";
      const showViewer  = block.dataset.showviewer === "true";

      if (!url) {
        block.textContent = "No SharePoint URL configured.";
        return;
      }

      if (!showViewer || !libraryName) {
        renderIframe(block, url, title);
        return;
      }

      // Derive SharePoint site URL: strip /{libraryName}/AllItems.aspx
      const siteUrl = url.replace(new RegExp(`/${libraryName}/AllItems\\.aspx.*$`, "i"), "");

      // Include FSObjType (0=file, 1=folder) and FileDirRef (parent path) for folder navigation
      const apiUrl = `${siteUrl}/_api/web/lists/getbytitle('${encodeURIComponent(libraryName)}')/items` +
                     `?$select=Title,FileLeafRef,EncodedAbsUrl,File_x0020_Type,FSObjType,FileDirRef&$top=2000`;

      // Use WP REST proxy to avoid CORS
      const proxyUrl = `/wp-json/brblocks/v1/sp-documents?api_url=${encodeURIComponent(apiUrl)}`;

      block.innerHTML = `<div class="sp-viewer-loading">
        <div class="sp-viewer-loading__spinner"></div>
        <p>Loading documents…</p>
      </div>`;

      fetch(proxyUrl)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) => {
          const docs = (data.value || []).map((item, i) => ({
            id:       String(i),
            title:    item.Title || item.FileLeafRef || `Item ${i + 1}`,
            link:     item.EncodedAbsUrl || "",
            type:     (item.File_x0020_Type || "").toLowerCase(),
            isFolder: item.FSObjType === 1,
            dirRef:   item.FileDirRef || "",
            leafRef:  item.FileLeafRef || "",
          }));
          renderViewer(block, title, docs, url);
        })
        .catch((err) => {
          console.warn("[sp-doc-lib] Proxy failed, falling back to iframe:", err);
          renderIframe(block, url, title);
        });
    });
  });


  // ── Iframe mode (fallback) ────────────────────────────────────────────────

  function renderIframe(block, url, title) {
    block.innerHTML = "";

    const header = document.createElement("div");
    header.className = "sp-custom-header";

    const titleEl = document.createElement("div");
    titleEl.className = "sp-header-title";
    titleEl.textContent = title;

    const link = document.createElement("a");
    link.className = "sp-header-btn";
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = "Open in new tab";

    header.appendChild(titleEl);
    header.appendChild(link);

    const wrapper = document.createElement("div");
    wrapper.className = "sp-doc-frame-wrapper";

    const iframe = document.createElement("iframe");
    iframe.src = url;
    iframe.className = "sp-doc-frame";

    wrapper.appendChild(iframe);
    block.appendChild(header);
    block.appendChild(wrapper);
  }


  // ── Viewer mode ───────────────────────────────────────────────────────────

  function renderViewer(block, titleText, allDocs, allItemsUrl) {
    block.innerHTML = "";

    const isMobile = () => window.matchMedia("(max-width: 767px)").matches;

    // Extract library server-relative root path from the AllItems URL
    let libraryRootPath = "";
    try {
      const urlObj = new URL(allItemsUrl);
      libraryRootPath = urlObj.pathname.replace(/\/AllItems\.aspx.*$/i, "").replace(/\/$/, "");
    } catch (e) {}

    // ── Helpers ──

    const detectType = (url, fallback = "file") => {
      if (!url) return fallback;
      const ext = (url.split("?")[0].split("#")[0].split(".").pop() || "").toLowerCase();
      if (ext === "pdf")           return "pdf";
      if (/^docx?$/.test(ext))     return "doc";
      if (/^xlsx?$/.test(ext))     return "xls";
      if (/^csv$/.test(ext))       return "csv";
      if (/^pptx?$/.test(ext))     return "ppt";
      return fallback;
    };

    const getPreviewType = (url, type) => {
      if (!url || url === "#") return "none";
      const ext = (url.split("?")[0].split("#")[0].split(".").pop() || "").toLowerCase();
      if (type === "pdf" || ext === "pdf")                                return "pdf";
      if (/^html?$/.test(ext))                                            return "html";
      if (/^(jpe?g|png|gif|webp|svg)$/.test(ext))                        return "image";
      if (/^(docx?|pptx?|xlsx?)$/.test(ext) ||
          type === "doc" || type === "xls" || type === "ppt")             return "office";
      return "none";
    };

    const iconSVG = (typeRaw, isFolder = false) => {
      if (isFolder) {
        return `<svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
          <path d="M4 24a5 5 0 0 1 5-5h15l5 5.5h22a5 5 0 0 1 5 5V52a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V24z"
                fill="#e5f5fb" stroke="#009cd6" stroke-width="2.5" stroke-linejoin="round"/>
        </svg>`;
      }
      const type = (typeRaw || "file").toLowerCase();
      const colors = {
        pdf:  { badge: "#E53935", text: "PDF" },
        doc:  { badge: "#1E88E5", text: "DOC" },
        xls:  { badge: "#2E7D32", text: "XLS" },
        csv:  { badge: "#6D4C41", text: "CSV" },
        ppt:  { badge: "#D84315", text: "PPT" },
        file: { badge: "#009cd6", text: "FILE" },
      };
      const c = colors[type] || colors.file;
      return `<svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <path d="M14 6h25l12 12v36a6 6 0 0 1-6 6H14a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6z" fill="#F4F6F8" stroke="#009cd6"/>
        <path d="M39 6v10a6 6 0 0 0 6 6h10L39 6z" fill="#e5f5fb"/>
        <rect x="18" y="26" width="28" height="2" rx="1.5" fill="#009cd6"/>
        <rect x="18" y="32" width="24" height="2" rx="1.5" fill="#009cd6"/>
        <rect x="18" y="38" width="20" height="2" rx="1.5" fill="#009cd6"/>
        <rect x="16" y="51" width="28" height="12" rx="6" fill="${c.badge}"/>
        <text x="30" y="61" text-anchor="middle" font-size="8.5"
              font-family="system-ui,-apple-system,Segoe UI,Roboto,Arial"
              fill="#FFFFFF" font-weight="700" letter-spacing=".5">${c.text}</text>
      </svg>`;
    };

    const iconDownload = `<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" style="flex-shrink:0">
      <path d="M12 16l-5-5h3V4h4v7h3l-5 5z" fill="currentColor"/>
      <path d="M5 19h14v2H5z" fill="currentColor"/>
    </svg>`;

    const iconOpenTab = `<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" style="flex-shrink:0">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <polyline points="15 3 21 3 21 9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>`;

    const makeDownloadBtn = (link, label, className) => {
      const btn = document.createElement("a");
      btn.className = className || "sp-viewer-btn";
      btn.href = link || "#";
      btn.rel = "noopener noreferrer";
      btn.innerHTML = `${iconDownload} ${label || "Download"}`;
      if (isMobile()) { btn.target = "_blank"; }
      else            { btn.setAttribute("download", ""); }
      return btn;
    };

    // ── Block header ──

    const blockHeader = document.createElement("div");
    blockHeader.className = "sp-custom-header";

    const blockTitle = document.createElement("div");
    blockTitle.className = "sp-header-title";
    blockTitle.textContent = titleText;

    const spLink = document.createElement("a");
    spLink.className = "sp-header-btn";
    spLink.href = allItemsUrl;
    spLink.target = "_blank";
    spLink.rel = "noopener noreferrer";
    spLink.textContent = "Open in SharePoint";

    blockHeader.appendChild(blockTitle);
    blockHeader.appendChild(spLink);
    block.appendChild(blockHeader);

    // ── Split layout ──

    const layout = document.createElement("div");
    layout.className = "sp-viewer-layout";
    block.appendChild(layout);

    // Sidebar
    const sidebar = document.createElement("div");
    sidebar.className = "sp-viewer-sidebar";

    // Breadcrumb
    const breadcrumbEl = document.createElement("div");
    breadcrumbEl.className = "sp-viewer-breadcrumb";
    sidebar.appendChild(breadcrumbEl);

    // Search
    const searchWrapper = document.createElement("div");
    searchWrapper.className = "sp-viewer-search";
    searchWrapper.innerHTML = `<svg class="sp-viewer-search__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2"/>
      <path d="M16 16l4.5 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>`;
    const searchInput = document.createElement("input");
    searchInput.type = "search";
    searchInput.className = "sp-viewer-search__input";
    searchInput.placeholder = "Search all documents…";
    searchInput.setAttribute("aria-label", "Search documents");
    searchWrapper.appendChild(searchInput);
    sidebar.appendChild(searchWrapper);

    const listContainer = document.createElement("div");
    listContainer.className = "sp-viewer-list";
    listContainer.style.cssText = "margin-block-start:0!important;margin-top:0!important;padding-top:0!important;";
    sidebar.appendChild(listContainer);

    // Viewer panel
    const panel = document.createElement("div");
    panel.className = "sp-viewer-panel";

    layout.appendChild(sidebar);
    layout.appendChild(panel);

    // ── Navigation state ──

    let currentPath      = libraryRootPath;
    let breadcrumbStack  = [{ name: titleText, path: libraryRootPath }];
    let activeDocId      = null;
    let currentQuery     = "";

    const renderBreadcrumb = () => {
      breadcrumbEl.innerHTML = "";
      breadcrumbStack.forEach((crumb, index) => {
        const isLast = index === breadcrumbStack.length - 1;

        if (index > 0) {
          const sep = document.createElement("span");
          sep.className = "sp-viewer-breadcrumb__sep";
          sep.textContent = "›";
          breadcrumbEl.appendChild(sep);
        }

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "sp-viewer-breadcrumb__item" + (isLast ? " sp-viewer-breadcrumb__item--active" : "");
        btn.textContent = crumb.name;
        btn.title = crumb.name;

        if (!isLast) {
          btn.addEventListener("click", () => navigateTo(crumb.path, null, index));
        }

        breadcrumbEl.appendChild(btn);
      });
    };

    const navigateTo = (path, name, truncateAt = null) => {
      currentPath = path;
      if (truncateAt !== null) {
        breadcrumbStack = breadcrumbStack.slice(0, truncateAt + 1);
      } else if (name) {
        breadcrumbStack.push({ name, path });
      }
      // Clear search and active doc when navigating
      currentQuery = "";
      searchInput.value = "";
      activeDocId = null;
      showPlaceholder();
      renderBreadcrumb();
      renderList();
    };

    const showPlaceholder = () => {
      panel.innerHTML = `<div class="sp-viewer-placeholder">
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <path d="M14 6h25l12 12v36a6 6 0 0 1-6 6H14a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6z" fill="#f0f4f8" stroke="#d0dae6" stroke-width="2"/>
          <path d="M39 6v10a6 6 0 0 0 6 6h10L39 6z" fill="#e5f5fb"/>
          <rect x="18" y="26" width="28" height="2" rx="1.5" fill="#c5d4e0"/>
          <rect x="18" y="32" width="24" height="2" rx="1.5" fill="#c5d4e0"/>
          <rect x="18" y="38" width="20" height="2" rx="1.5" fill="#c5d4e0"/>
        </svg>
        <p>Select a document to preview</p>
      </div>`;
    };

    const loadViewer = (doc) => {
      activeDocId = doc.id;
      const type        = doc.type || detectType(doc.link);
      const previewType = getPreviewType(doc.link, type);

      listContainer.querySelectorAll(".sp-viewer-doc-item").forEach((btn) => {
        btn.classList.toggle("is-active", btn.dataset.docId === doc.id);
      });

      panel.innerHTML = "";
      const content = document.createElement("div");
      content.className = "sp-viewer-content";

      // Doc header: title + Open + Download
      const docHeader = document.createElement("div");
      docHeader.className = "sp-viewer-doc-header";

      const titleEl = document.createElement("span");
      titleEl.className = "sp-viewer-doc-title";
      titleEl.textContent = doc.title || "Document";

      const actions = document.createElement("div");
      actions.className = "sp-viewer-actions";

      const openBtn = document.createElement("a");
      openBtn.className = "sp-viewer-btn";
      openBtn.href = doc.link || "#";
      openBtn.target = "_blank";
      openBtn.rel = "noopener noreferrer";
      openBtn.innerHTML = `${iconOpenTab} Open`;

      actions.appendChild(openBtn);
      actions.appendChild(makeDownloadBtn(doc.link, "Download", "sp-viewer-btn"));
      docHeader.appendChild(titleEl);
      docHeader.appendChild(actions);
      content.appendChild(docHeader);

      const body = document.createElement("div");
      body.className = "sp-viewer-body";

      if (previewType === "pdf" || previewType === "html") {
        const iframe = document.createElement("iframe");
        iframe.className = "sp-viewer-iframe";
        iframe.src = doc.link;
        iframe.setAttribute("title", doc.title || "Document preview");
        body.appendChild(iframe);

      } else if (previewType === "office") {
        const officeUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(doc.link)}`;
        const noPreview = document.createElement("div");
        noPreview.className = "sp-viewer-no-preview";

        const iconEl = document.createElement("div");
        iconEl.className = "sp-viewer-doc-icon";
        iconEl.innerHTML = iconSVG(type || "file");

        const msg = document.createElement("p");
        msg.textContent = "Inline preview not available for this file type.";

        const officeBtn = document.createElement("a");
        officeBtn.className = "sp-viewer-btn sp-viewer-btn--large";
        officeBtn.href = officeUrl;
        officeBtn.target = "_blank";
        officeBtn.rel = "noopener noreferrer";
        officeBtn.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M14 3v4a1 1 0 0 0 1 1h4" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" stroke-width="1.8" fill="none"/></svg> Open in Office Online`;

        noPreview.appendChild(iconEl);
        noPreview.appendChild(msg);
        noPreview.appendChild(officeBtn);
        noPreview.appendChild(makeDownloadBtn(doc.link, "Download", "sp-viewer-btn"));
        body.appendChild(noPreview);

      } else if (previewType === "image") {
        const img = document.createElement("img");
        img.className = "sp-viewer-image";
        img.src = doc.link;
        img.alt = doc.title || "";
        body.appendChild(img);

      } else {
        const noPreview = document.createElement("div");
        noPreview.className = "sp-viewer-no-preview";

        const iconEl = document.createElement("div");
        iconEl.className = "sp-viewer-doc-icon";
        iconEl.innerHTML = iconSVG(type || "file");

        const msg = document.createElement("p");
        msg.textContent = "No preview available for this file type.";

        noPreview.appendChild(iconEl);
        noPreview.appendChild(msg);
        noPreview.appendChild(makeDownloadBtn(doc.link, "Download", "sp-viewer-btn sp-viewer-btn--large"));
        body.appendChild(noPreview);
      }

      content.appendChild(body);
      panel.appendChild(content);
    };

    const renderList = () => {
      listContainer.innerHTML = "";

      let items;
      if (currentQuery) {
        // Search: show all matching files across the whole library (no folders)
        items = allDocs.filter((doc) =>
          !doc.isFolder &&
          (doc.title || doc.leafRef || "").toLowerCase().includes(currentQuery)
        );
      } else {
        // Normal: show contents of current folder — folders first, then files alphabetically
        items = allDocs
          .filter((doc) => doc.dirRef === currentPath)
          .sort((a, b) => {
            if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
            return (a.title || a.leafRef || "").localeCompare(b.title || b.leafRef || "");
          });
      }

      if (!items.length) {
        listContainer.innerHTML = `<div class="sp-viewer-no-results">
          ${currentQuery ? "No documents found." : "This folder is empty."}
        </div>`;
        if (!activeDocId) showPlaceholder();
        return;
      }

      items.forEach((doc) => {
        const type = doc.isFolder ? "folder" : (doc.type || detectType(doc.link)).toLowerCase();
        const btn  = document.createElement("button");
        btn.type = "button";
        btn.className = "sp-viewer-doc-item" + (doc.isFolder ? " sp-viewer-doc-item--folder" : "");
        if (!doc.isFolder) btn.dataset.docId = doc.id;

        const iconSpan = document.createElement("span");
        iconSpan.className = "sp-viewer-doc-icon";
        iconSpan.innerHTML = iconSVG(type, doc.isFolder);

        const titleSpan = document.createElement("span");
        titleSpan.className = "sp-viewer-item-title";
        titleSpan.textContent = doc.title || doc.leafRef || "";

        btn.appendChild(iconSpan);
        btn.appendChild(titleSpan);

        if (doc.isFolder) {
          const chevron = document.createElement("span");
          chevron.className = "sp-viewer-folder-chevron";
          chevron.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`;
          btn.appendChild(chevron);
        }

        btn.addEventListener("click", () => {
          if (doc.isFolder) {
            // Navigate into folder
            const folderPath = doc.dirRef + "/" + doc.leafRef;
            navigateTo(folderPath, doc.title || doc.leafRef);
          } else if (isMobile()) {
            if (doc.link && doc.link !== "#") {
              window.open(doc.link, "_blank", "noopener,noreferrer");
            }
          } else {
            loadViewer(doc);
          }
        });

        if (!doc.isFolder && doc.id === activeDocId) {
          btn.classList.add("is-active");
        }

        listContainer.appendChild(btn);
      });

      // Auto-load first file on desktop (no active doc yet)
      if (!isMobile() && !activeDocId) {
        const firstFile = items.find((d) => !d.isFolder);
        if (firstFile) loadViewer(firstFile);
        else showPlaceholder();
      }
    };

    // Initial render
    renderBreadcrumb();
    renderList();

    let debounceTimer;
    searchInput.addEventListener("input", () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        currentQuery = searchInput.value.trim().toLowerCase();
        renderList();
      }, 150);
    });
  }

})();
